import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress, getTokenAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import type { TransactionHash, TokenId } from "@/types";
import { getKyberRouterData } from "../router/kyberRouter.service";
import { prepareFormTokenAmountDecimals } from "@/utils/token";

/**
 * Prepares a zap in and stake transaction with router data
 */
export const prepareZapInAndStakeTransaction = async (
    tokenId: TokenId,
    amount: number,
    to: string,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const tokenAddress = getTokenAddress(walletClient.chain.id, tokenId);
    const amountBigInt = prepareFormTokenAmountDecimals(amount, tokenId);
    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    try {
        const routerData = await getKyberRouterData(tokenId, amount, walletClient);

        return {
            to: contractAddress,
            data: encodeFunctionData({
                abi: gatewayV1Abi,
                functionName: "zapInAndStake",
                args: [tokenAddress, amountBigInt, routerData.routerAddress, routerData.callData, to],
            }),
        };
    } catch (error) {
        throw new Error(
            `Failed to prepare zapInAndStake transaction for ${tokenId}: ${
                error instanceof Error ? error.message : String(error)
            }`,
        );
    }
};

/**
 * Estimates gas for a zap in and stake transaction
 */
export const estimateZapInAndStakeTransactionGas = async (
    tokenId: TokenId,
    amount: number,
    to: string,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareZapInAndStakeTransaction(tokenId, amount, to, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};

/**
 * Executes a zap in and stake transaction
 */
export const gatewayZapInAndStakeTransaction = async (
    tokenId: TokenId,
    amount: number,
    to: string,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareZapInAndStakeTransaction(tokenId, amount, to, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};
